import { addonsApi, useGetAddonsDataQuery } from '@/src/api/rtkQuery/addonsApi'

import { useGetItineraryDataQuery } from '@/src/api/rtkQuery/itineraryApi'
import useFormatCurrencyForItinerary from '@/src/hooks/currency/useFormatCurrencyForItinerary'
import { useAppDispatch } from '@/src/redux/store'
import { MealInfo, SelectedAncillaryType } from '@/src/types/itinerary'
import { useCallback, useState } from 'react'
import useGetItineraryId from '../../useGetItineraryId'

const useMealProcessor = () => {
    const itineraryId = useGetItineraryId()
    const { formatCurrency } = useFormatCurrencyForItinerary()
    const { data: addonsData } = useGetAddonsDataQuery({ itineraryId })
    const dispatch = useAppDispatch()

    const { paxCount } = useGetItineraryDataQuery(
        { itineraryId },
        {
            selectFromResult: ({ data }) => {
                try {
                    if (data) {
                        return {
                            paxCount: data.searchCriteria.paxInfo.adults + data.searchCriteria.paxInfo.children
                        }
                    }
                    return {
                        paxCount: 0
                    }
                } catch (e) {
                    return {
                        paxCount: 0
                    }
                }
            }
        }
    )

    const [selectedSegmentIndex, setSelectedSegmentIndex] = useState<number>(0)

    const { journeyAncillaries, selectedAncillaries = [] } = addonsData || {}
    const { mealAncillaries } = journeyAncillaries || {}
    const { availableSegments = [], segmentWiseAncillaryData } = mealAncillaries || {}

    const selectedSegment = availableSegments?.[selectedSegmentIndex] || { from: '', to: '', key: '' }
    const mealOptions = segmentWiseAncillaryData?.[selectedSegment.key] || []
    const selectedMealOptions =
        selectedAncillaries.find(
            ancillary =>
                ancillary.segmentOriginDestinationInfo.from === selectedSegment.from &&
                ancillary.segmentOriginDestinationInfo.to === selectedSegment.to
        )?.mealDetails || []

    const handleAddMeal = useCallback(
        (meal: MealInfo) => {
            dispatch(
                addonsApi.util.updateQueryData('getAddonsData', { itineraryId }, draft => {
                    if (!draft.selectedAncillaries) {
                        draft.selectedAncillaries = []
                    }

                    const selectedAncillarySegmentIndex = draft.selectedAncillaries.findIndex(
                        ancillary =>
                            ancillary.segmentOriginDestinationInfo.from === selectedSegment.from &&
                            ancillary.segmentOriginDestinationInfo.to === selectedSegment.to
                    )

                    if (selectedAncillarySegmentIndex !== -1) {
                        // Update existing segment
                        draft.selectedAncillaries[selectedAncillarySegmentIndex].mealDetails.push(meal)
                    } else {
                        // Add new segment
                        draft.selectedAncillaries.push({
                            segmentOriginDestinationInfo: {
                                from: selectedSegment.from,
                                to: selectedSegment.to
                            },
                            mealDetails: [meal],
                            baggageDetails: [],
                            seatDetails: []
                        } as SelectedAncillaryType)
                    }
                })
            )
        },
        [itineraryId, selectedSegment]
    )

    const handleRemoveMeal = useCallback(
        (mealCode: MealInfo) => {
            dispatch(
                addonsApi.util.updateQueryData('getAddonsData', { itineraryId }, draft => {
                    if (!draft.selectedAncillaries) return

                    const selectedAncillarySegmentIndex = draft.selectedAncillaries.findIndex(
                        ancillary =>
                            ancillary.segmentOriginDestinationInfo.from === selectedSegment.from &&
                            ancillary.segmentOriginDestinationInfo.to === selectedSegment.to
                    )

                    if (selectedAncillarySegmentIndex !== -1) {
                        // Find the first occurrence of the meal to remove
                        const mealIndex = draft.selectedAncillaries[
                            selectedAncillarySegmentIndex
                        ].mealDetails.findIndex(meal => meal.code === mealCode.code)

                        // Remove only the first occurrence if found
                        if (mealIndex !== -1) {
                            draft.selectedAncillaries[selectedAncillarySegmentIndex].mealDetails.splice(mealIndex, 1)
                        }
                    }
                })
            )
        },
        [itineraryId, selectedSegment]
    )

    return {
        selectedSegment,
        availableSegments,
        selectedSegmentIndex,
        mealOptions,
        paxCount,
        selectedMealOptions,
        setSelectedSegmentIndex,
        handleAddMeal,
        handleRemoveMeal,
        formatCurrency
    }
}

export default useMealProcessor
